import React from 'react';
import { NavLink } from 'react-router-dom';
import { FiHome, FiFileText, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../../../context/AuthContext';
import styles from './Sidebar.module.css';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  allowedRoles?: string[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: <FiHome />,
      allowedRoles: [
        'superadmin',
        'admin',
        'editor',
        'developer',
        'executive',
        'manager',
        'blogger',
        'affiliater',
      ],
    },
    {
      path: '/top-news',
      label: 'Top News',
      icon: <FiTrendingUp />,
      allowedRoles: [
        'superadmin',
        'admin',
        'editor',
        'developer',
        'executive',
        'manager',
        'blogger',
        'affiliater',
      ],
    },
    {
      path: '/articles',
      label: 'Articles',
      icon: <FiFileText />,
      allowedRoles: [
        'superadmin',
        'admin',
        'editor',
        'developer',
        'blogger',
      ],
    },
    {
      path: '/team',
      label: 'Team',
      icon: <FiUsers />,
      allowedRoles: ['superadmin', 'admin', 'developer'],
    },
  ];

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter((item) => {
    if (!item.allowedRoles) return true;
    return user && item.allowedRoles.includes(user.role);
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className={styles.overlay} onClick={onClose} />}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
        {/* Header / Logo */}
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>🚀</span>
            <div className={styles.brandText}>
              <span className={styles.brand}>Startup</span>
              <span className={styles.brandHighlight}>Rahi</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className={styles.nav}>
          <ul className={styles.navList}>
            {filteredNavItems.map((item) => (
              <li key={item.path} className={styles.navItem}>
                <NavLink
                  to={item.path}
                  onClick={onClose} // close sidebar on mobile
                  className={({ isActive }) =>
                    isActive
                      ? `${styles.navLink} ${styles.active}`
                      : styles.navLink
                  }
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  <span className={styles.navLabel}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer / User Info */}
        <div className={styles.sidebarFooter}>
          <div className={styles.userInfo}>
            <img
              src={
                user?.profilePhoto?.url ||
                `https://ui-avatars.com/api/?name=${user?.name}`
              }
              alt={user?.name}
              className={styles.userAvatar}
            />
            <div className={styles.userDetails}>
              <p className={styles.userName}>{user?.name}</p>
              <p className={styles.userRole}>{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
